import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { posthogKey, posthogOptions } from "./posthogConfig";

// SPA error monitoring. Privacy rules for a screening product
// (mirrors the Worker's observability.ts):
//   * No DSN (local dev) -> Sentry.init is a harmless no-op.
//   * sendDefaultPii stays false; no Replay; no user email.
//   * beforeSend strips the URL query string. The screening query
//     never reaches the SPA (it goes client -> Worker API), but the
//     scrub is a structural guard, not a hope.
// PostHog (product analytics, consent-gated) is intentionally NOT here
// — it lands in a separate, later change.
function envStr(key: string): string {
  const v = (import.meta.env as Record<string, string | undefined>)[key];
  return v && v.length > 0 ? v : "";
}

export function initSentry(): void {
  const dsn = envStr("VITE_SENTRY_DSN");
  if (!dsn) return;
  const release = envStr("VITE_RELEASE");
  const options: Sentry.BrowserOptions = {
    dsn,
    environment: envStr("VITE_SENTRY_ENV") || "production",
    sendDefaultPii: false,
    tracesSampleRate: 0,
    beforeSend(event) {
      const req = event.request;
      if (req) {
        if (typeof req.url === "string")
          req.url = req.url.split("?")[0] ?? req.url;
        delete req.query_string;
        delete req.cookies;
        delete req.headers;
        delete req.data;
      }
      delete event.user;
      return event;
    },
  };
  if (release) options.release = release;
  Sentry.init(options);
}

export const ErrorBoundary = Sentry.ErrorBoundary;

// ---- PostHog product analytics (consent-gated, EU) -----------------
// Never initialised until the user explicitly accepts. Reject (or no
// decision) => PostHog is never loaded and sets no cookies.
const CONSENT_KEY = "pg_consent";
type Consent = "granted" | "denied";

export function consentDecision(): Consent | null {
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

function setDecision(v: Consent): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, v);
  } catch {
    /* storage blocked: treated as undecided next load */
  }
}

// Banner only makes sense when analytics is actually configured.
export function analyticsAvailable(): boolean {
  return posthogKey() !== "";
}

let started = false;
function start(): void {
  const key = posthogKey();
  if (!key || started) return;
  started = true;
  posthog.init(
    key,
    posthogOptions() as unknown as Parameters<typeof posthog.init>[1],
  );
}

export function initPostHogIfConsented(): void {
  if (consentDecision() === "granted") start();
}

export function grantConsent(): void {
  setDecision("granted");
  start();
}

export function revokeConsent(): void {
  setDecision("denied");
  try {
    posthog.opt_out_capturing();
    posthog.reset(true);
  } catch {
    /* not initialised — nothing to opt out of */
  }
}

// Opaque account id only — never email or any PII.
export function identifyAccount(accountId: string): void {
  if (started && consentDecision() === "granted") {
    try {
      posthog.identify(accountId);
    } catch {
      /* init race — safe to skip; next event still anonymous */
    }
  }
}

// Funnel instrumentation (A11): explicit product events, guarded exactly
// like identifyAccount — a no-op unless PostHog is loaded AND the visitor
// granted consent. Properties must stay non-PII (plan/interval/booleans
// only — never email or any WorkOS/company identifier).
export function captureEvent(
  name: string,
  properties?: Record<string, unknown>,
): void {
  if (started && consentDecision() === "granted") {
    try {
      posthog.capture(name, properties);
    } catch {
      /* init race — safe to skip; not worth blocking the UI on */
    }
  }
}
